/**
 * Admin Brix Outliers page — analysis-only report.
 *
 * Surfaces every recipe in the catalog with its stored `target_brix`, the
 * volume-weighted `resulting_brix` computed from its ingredients, the
 * recommended band for its type, and a status label so the admin can
 * decide where the real fault lies (target / ingredient brix / amounts /
 * type) BEFORE doing any batch correction.
 *
 * Strictly read-only — does NOT mutate any recipe data.
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { API } from '../App';

const STATUS_META = {
  ok: {
    label: 'OK',
    cls: 'bg-green-100 text-green-800 border-green-200',
  },
  target_out_of_range: {
    label: 'target_brix udenfor interval',
    cls: 'bg-amber-100 text-amber-800 border-amber-200',
  },
  resulting_out_of_range: {
    label: 'resulting_brix udenfor interval',
    cls: 'bg-orange-100 text-orange-800 border-orange-200',
  },
  large_diff: {
    label: 'stor afvigelse target vs beregnet',
    cls: 'bg-red-100 text-red-800 border-red-200',
  },
};

const fmtBrix = (v) => (v === null || v === undefined ? '—' : `${v.toFixed(2)}°Bx`);

const AdminBrixOutliersPage = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [bands, setBands] = useState({});
  const [environment, setEnvironment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  // Backend-side filters (refetch when toggled). Both OFF by default so the
  // report focuses on live, user-facing recipes — admins can opt back in.
  const [includeUnpublished, setIncludeUnpublished] = useState(false);
  const [includeTestData, setIncludeTestData] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const r = await axios.get(`${API}/admin/brix-outliers`, {
          params: {
            include_unpublished: includeUnpublished,
            include_test_data: includeTestData,
          },
        });
        if (cancelled) return;
        setRows(r.data.rows || []);
        setSummary(r.data.summary || null);
        setBands(r.data.bands || {});
        setEnvironment(r.data.environment || null);
      } catch (err) {
        toast.error('Kunne ikke hente Brix-outliers');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [includeUnpublished, includeTestData]);

  const types = useMemo(() => {
    const s = new Set(rows.map((r) => r.type));
    return ['all', ...Array.from(s).sort()];
  }, [rows]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (typeFilter !== 'all' && r.type !== typeFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!(r.name || '').toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [rows, statusFilter, typeFilter, search]);

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl" data-testid="admin-brix-outliers-page">
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <h1 className="text-3xl font-bold text-gray-900">Brix-outliers</h1>
          {environment && (
            <EnvironmentBadge env={environment} />
          )}
        </div>
        <p className="text-gray-600">
          Analyse-rapport — hver opskrifts target_brix sammenlignet med dens
          beregnede volumen-vægtede resulting_brix og det anbefalede interval
          for typen. <strong>Ingen ændringer foretages.</strong>
        </p>
        {environment?.is_preview && (
          <div
            className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
            data-testid="brix-outliers-preview-warning"
          >
            ⚠️ Du kigger på <strong>preview-databasen</strong> (<code>{environment.db_name}</code>).
            Tallene afspejler ikke nødvendigvis production-data. For at se den
            samme rapport mod live-data, åbn den samme side på det deployede
            production-domæne (samme route, samme auth).
          </div>
        )}
      </div>

      {/* Backend-side filter toggles — refetch on change */}
      <div className="flex flex-wrap items-center gap-4 mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm">
        <span className="font-semibold text-blue-900">Datasæt:</span>
        <label className="flex items-center gap-2 cursor-pointer text-blue-900">
          <input
            type="checkbox"
            checked={includeTestData}
            onChange={(e) => setIncludeTestData(e.target.checked)}
            data-testid="brix-outliers-include-test"
          />
          Inkluder testdata (Pytest, TEST_…)
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-blue-900">
          <input
            type="checkbox"
            checked={includeUnpublished}
            onChange={(e) => setIncludeUnpublished(e.target.checked)}
            data-testid="brix-outliers-include-unpublished"
          />
          Inkluder upublicerede opskrifter
        </label>
        {summary && (
          <span className="ml-auto text-xs text-blue-700" data-testid="brix-outliers-skipped">
            Skipped: {summary.skipped_test_data} testdata · {summary.skipped_unpublished} upublicerede
          </span>
        )}
      </div>

      {/* Summary tiles */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="brix-outliers-summary">
          <SummaryTile label="I alt" value={summary.total} cls="bg-gray-50 text-gray-900" testid="brix-summary-total" />
          <SummaryTile label="OK" value={summary.ok} cls="bg-green-50 text-green-800" testid="brix-summary-ok" />
          <SummaryTile label="target ud" value={summary.target_out_of_range} cls="bg-amber-50 text-amber-800" testid="brix-summary-target-out" />
          <SummaryTile label="beregnet ud" value={summary.resulting_out_of_range} cls="bg-orange-50 text-orange-800" testid="brix-summary-resulting-out" />
          <SummaryTile label="stor afvigelse" value={summary.large_diff} cls="bg-red-50 text-red-800" testid="brix-summary-large-diff" />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-white border border-gray-200 rounded-lg">
        <input
          type="text"
          placeholder="Søg navn…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm flex-1 min-w-[160px]"
          data-testid="brix-outliers-search"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
          data-testid="brix-outliers-status-filter"
        >
          <option value="all">Alle statuser</option>
          <option value="ok">OK</option>
          <option value="target_out_of_range">target_brix udenfor</option>
          <option value="resulting_out_of_range">resulting_brix udenfor</option>
          <option value="large_diff">stor afvigelse</option>
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
          data-testid="brix-outliers-type-filter"
        >
          {types.map((t) => (
            <option key={t} value={t}>
              {t === 'all' ? 'Alle typer' : t}
            </option>
          ))}
        </select>
        <span className="text-xs text-gray-500 ml-auto" data-testid="brix-outliers-count">
          {filtered.length} af {rows.length}
        </span>
      </div>

      {/* Bands legend */}
      {Object.keys(bands).length > 0 && (
        <details className="mb-4 text-xs text-gray-600 bg-gray-50 rounded-md p-3">
          <summary className="cursor-pointer font-semibold">Anbefalede Brix-intervaller pr. type</summary>
          <ul className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
            {Object.entries(bands).map(([t, [lo, hi]]) => (
              <li key={t}><strong>{t}:</strong> {lo}–{hi} °Bx</li>
            ))}
          </ul>
        </details>
      )}

      {/* Table */}
      {loading ? (
        <div className="p-8 text-center text-gray-500">Indlæser…</div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-700">
              <tr>
                <th className="px-3 py-2 font-semibold">Opskrift</th>
                <th className="px-3 py-2 font-semibold">Type</th>
                <th className="px-3 py-2 font-semibold">Family</th>
                <th className="px-3 py-2 font-semibold text-right">target</th>
                <th className="px-3 py-2 font-semibold text-right">beregnet</th>
                <th className="px-3 py-2 font-semibold text-right">interval</th>
                <th className="px-3 py-2 font-semibold text-right">diff</th>
                <th className="px-3 py-2 font-semibold">status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.ok;
                return (
                  <tr key={r.id} data-testid={`brix-outliers-row-${r.id}`} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <Link
                        to={`/recipes/${r.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-700 hover:underline"
                      >
                        {r.name}
                      </Link>
                      {r.alcohol_flag && (
                        <span className="ml-1.5 text-[10px] uppercase tracking-wide text-amber-700">alkohol</span>
                      )}
                      {!r.is_published && (
                        <span className="ml-1.5 text-[10px] uppercase tracking-wide text-gray-400">upubl</span>
                      )}
                      {r.missing_brix_ingredients?.length > 0 && (
                        <div className="text-[11px] text-red-600 mt-0.5">
                          mangler brix: {r.missing_brix_ingredients.join(', ')}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-700">{r.type}</td>
                    <td className="px-3 py-2 text-gray-500">{r.family || '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums" data-testid={`brix-target-${r.id}`}>
                      {fmtBrix(r.target_brix)}
                      {r.target_out_of_range && <span className="ml-1 text-amber-600" title="target udenfor interval">⚠</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums" data-testid={`brix-resulting-${r.id}`}>
                      {fmtBrix(r.resulting_brix)}
                      {r.resulting_out_of_range && <span className="ml-1 text-orange-600" title="beregnet udenfor interval">⚠</span>}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-500 tabular-nums whitespace-nowrap">
                      {r.recommended_min}–{r.recommended_max}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">
                      {r.diff === null || r.diff === undefined ? '—' : `${r.diff.toFixed(2)}`}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs border ${meta.cls}`}>
                        {meta.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && !loading && (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-gray-500">
                    Ingen rækker matcher filtrene.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-4 text-xs text-gray-500">
        Formel: <code>resulting_brix = Σ(ingrediens.brix × ml) / Σ ml</code>.
        Ingrediens-Brix læses fra opskriftens egen ingrediens, ellers fra
        ingrediens-katalogets <code>default_brix</code> / <code>standard_brix</code>.
        Klik på en opskriftstitel for at åbne den i en ny fane.
      </p>
    </div>
  );
};

const SummaryTile = ({ label, value, cls, testid }) => (
  <div className={`rounded-lg p-3 ${cls}`} data-testid={testid}>
    <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
    <div className="text-2xl font-bold tabular-nums">{value}</div>
  </div>
);

const EnvironmentBadge = ({ env }) => {
  const isPreview = env?.is_preview;
  const cls = isPreview
    ? 'bg-amber-100 text-amber-800 border-amber-300'
    : 'bg-green-100 text-green-800 border-green-300';
  const label = isPreview ? 'PREVIEW' : 'PRODUCTION';
  return (
    <span
      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-bold tracking-wider ${cls}`}
      data-testid="brix-outliers-env-badge"
      title={`MongoDB: ${env?.db_name || '?'}`}
    >
      <span className={`inline-block w-2 h-2 rounded-full ${isPreview ? 'bg-amber-500' : 'bg-green-500'}`} />
      {label}
      <span className="opacity-60 font-mono normal-case tracking-normal">
        {env?.db_name}
      </span>
    </span>
  );
};

export default AdminBrixOutliersPage;
